import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { computeGeometry } from "./geometry";
import { STR } from "../i18n";
import { destLabel, DEST_CODES, EMPTY_DEST } from "../lib/destinations";
import { synthesizeBogies, hasBogieImbalance } from "../lib/bogie";
import { applyOcrResolution } from "./ocrResolve";
import { STATIONS } from "./stations";

export { STATIONS };

const PHOTO =
  "https://images.unsplash.com/photo-1594206185054-1ca85fbb6021?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjb2FsJTIwdHJhaW4lMjB3YWdvbiUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzgyNTg0NDg4fDA&ixlib=rb-4.1.0&q=85";

let _id = 1;
const uid = () => `w${_id++}`;
const rnd = (a, b) => a + Math.random() * (b - a);
const ri = (a, b) => Math.floor(rnd(a, b + 1));
const gnum = () => `6${ri(0, 9)}${ri(100000, 999999)}`;
const nowS = () => Date.now() / 1000;
// дисплейное время простоя ускорено: 1 реальная секунда = 30 сим-секунд,
// иначе за сеанс просмотра ни один вагон не успевает выйти из зелёной зоны
const TIME_SCALE = 30;
// единственный способ получить простой наружу: dwell_since хранит реальный epoch,
// наружу выходит только сим-время через эту функцию
const simDwell = (since, t) => Math.max(0, Math.floor((t - since) * TIME_SCALE));

export function buildExportCSV(wagons, code, lang, now = nowS) {
  const L = STR[lang] || STR.en;
  const cargoL = { coal: L.coal, empty: L.empty, foreign: L.foreign };
  const rows = [[L.csvTrack, L.csvNumber, L.csvCargo, L.csvOwner, L.csvBrutto, L.csvNetto, L.csvDest, L.csvDwell, L.csvOcr]];
  wagons
    .filter((w) => w.st === code && w.phase !== "departing")
    .sort((a, b) => a.tk - b.tk || a.slot - b.slot)
    .forEach((w) => {
      rows.push([
        `${L.track} ${w.tk + 1}`,
        w.number,
        cargoL[w.cargo],
        w.owner === "own" ? L.own : L.csvForeign,
        w.weight_brutto,
        w.weight_netto,
        destLabel(w.destination, lang),
        Math.floor(simDwell(w.dwell_since, now()) / 60),
        w.ocr_confidence,
      ]);
    });
  return "\ufeff" + rows.map((r) => r.join(";")).join("\n");
}

function makeWagon(code, tk, slot, cargo) {
  cargo = cargo || (Math.random() < 0.66 ? "coal" : Math.random() < 0.7 ? "empty" : "foreign");
  let netto, brutto, owner, dest;
  if (cargo === "coal") {
    netto = +rnd(58, 71).toFixed(1);
    brutto = +(netto + rnd(22, 24)).toFixed(1);
    owner = "own";
    dest = DEST_CODES[ri(0, DEST_CODES.length - 1)];
  } else if (cargo === "empty") {
    netto = 0;
    brutto = +rnd(22, 24.5).toFixed(1);
    owner = "own";
    dest = EMPTY_DEST;
  } else {
    netto = +rnd(40, 68).toFixed(1);
    brutto = +(netto + rnd(22, 24)).toFixed(1);
    owner = "foreign";
    dest = DEST_CODES[ri(0, DEST_CODES.length - 1)];
  }
  const bogies = synthesizeBogies(brutto, { empty: cargo === "empty" });
  return {
    id: uid(),
    number: gnum(),
    st: code,
    tk,
    slot,
    cargo,
    weight_brutto: brutto,
    weight_netto: netto,
    bogie1_t: bogies.bogie1_t,
    bogie2_t: bogies.bogie2_t,
    destination: dest,
    owner,
    note: "",
    ocr_confidence: +rnd(0.62, 0.99).toFixed(2),
    photo_url: PHOTO,
    status: "parked",
    phase: "parked",
    // 1–60 sim minutes spans the 15 / 45 dwell bands so first paint is mixed
    dwell_since: nowS() - (ri(1, 60) * 60) / TIME_SCALE,
    updated_at: new Date().toISOString(),
    updated_by: "system",
    route: null,
    routeKey: 0,
  };
}

export function useYardSim(lang) {
  const wagonsRef = useRef([]);
  const eventsRef = useRef([]);
  const ocrRef = useRef([]);
  const timersRef = useRef([]);
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // per-station geometry (stable)
  const geos = useMemo(() => {
    const g = {};
    STATIONS.forEach((s) => (g[s.code] = computeGeometry(s.tracks, s.type === "sorting")));
    return g;
  }, []);

  const cap = useCallback(
    (code, tk) => {
      const g = geos[code];
      const base = code === "NPP" ? 13 : 10;
      return Math.min(base, g.slotsCount(tk) - 1);
    },
    [geos]
  );

  const countOn = useCallback((code, tk) => {
    return wagonsRef.current.filter((w) => w.st === code && w.tk === tk && w.phase !== "departing").length;
  }, []);

  // last wagon of the coupled chain (nearest the entry throat) — the only one that can be shunted out
  const tailSlot = useCallback((code, tk) => {
    const list = wagonsRef.current.filter((w) => w.st === code && w.tk === tk && w.phase !== "departing");
    return list.length ? Math.max(...list.map((w) => w.slot)) : -1;
  }, []);

  const pushEvent = useCallback((type, params = {}, severity = "info", sourceKey = "system") => {
    eventsRef.current = [
      { id: uid(), ts: new Date().toISOString(), type, params, severity, sourceKey },
      ...eventsRef.current,
    ].slice(0, 80);
  }, []);

  const seed = useCallback(() => {
    const arr = [];
    STATIONS.forEach((s) => {
      const g = geos[s.code];
      for (let tk = 0; tk < s.tracks; tk++) {
        const fill = s.type === "sorting" ? ri(4, cap(s.code, tk)) : ri(0, cap(s.code, tk) - 2);
        for (let k = 0; k < fill; k++) {
          const cargo = s.type === "loading" ? (Math.random() < 0.7 ? "coal" : "empty") : null;
          const w = makeWagon(s.code, tk, k, cargo);
          w.route = [g.rest(tk, k)];
          arr.push(w);
        }
      }
    });
    wagonsRef.current = arr;
  }, [geos, cap]);

  // ----- movement helpers -----
  const issueRoute = (w, route, phase) => {
    w.route = route;
    w.routeKey += 1;
    w.phase = phase;
    w.status = phase;
  };

  const onDone = useCallback(
    (id, phase) => {
      const w = wagonsRef.current.find((x) => x.id === id);
      if (!w) return;
      if (phase === "departing") {
        wagonsRef.current = wagonsRef.current.filter((x) => x.id !== id);
      } else {
        w.phase = "parked";
        w.status = "parked";
        // collapse route to the rest point so a remount never replays the entry animation
        if (w.route && w.route.length > 1) w.route = [w.route[w.route.length - 1]];
      }
      bump();
    },
    [bump]
  );

  const schedule = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
  };

  // spawn an arriving chain of wagons (staggered -> looks like a train pulling in)
  const spawnTrain = useCallback(
    (code, tk, dataList) => {
      const g = geos[code];
      dataList.forEach((data, j) => {
        schedule(() => {
          const slot = countOn(code, tk);
          if (slot >= cap(code, tk)) return; // no space -> skip, never overflow
          const w = { ...makeWagon(code, tk, slot, data.cargo), ...data, id: uid(), st: code, tk, slot };
          if (data.bogie1_t == null || data.bogie2_t == null) {
            Object.assign(w, synthesizeBogies(w.weight_brutto, { empty: w.cargo === "empty" }));
          }
          w.route = g.arrivingRoute(tk, slot);
          w.routeKey = 1;
          w.phase = "arriving";
          w.status = "arriving";
          w.dwell_since = nowS();
          wagonsRef.current = [...wagonsRef.current, w];
          bump();
        }, j * 420);
      });
    },
    [geos, countOn, cap, bump]
  );

  // depart all wagons on a track as a coupled chain
  const departTrack = useCallback(
    (code, tk) => {
      const g = geos[code];
      const list = wagonsRef.current
        .filter((w) => w.st === code && w.tk === tk && w.phase !== "departing")
        .sort((a, b) => a.slot - b.slot);
      list.forEach((w) => issueRoute(w, g.departingRoute(tk, w.slot), "departing"));
      bump();
      return list;
    },
    [geos, bump]
  );

  // ----- the tick -----
  const tick = useCallback(() => {
    const w = wagonsRef.current;
    const loadingStations = STATIONS.filter((s) => s.type === "loading");
    const sorting = STATIONS.find((s) => s.type === "sorting");

    const occupied = w.filter((x) => x.phase !== "departing").length;
    let capacity = 0;
    for (const s of STATIONS) {
      for (let tk = 0; tk < s.tracks; tk++) capacity += cap(s.code, tk);
    }

    const action = pick([
      ["arrive", arrivalWeight(occupied, capacity)],
      ["load", 4],
      ["toSort", 4],
      ["departOut", 6],
      ["shunt", 2],
      ["ocr", 2],
    ]);

    if (action === "arrive") {
      const open = [];
      for (const s of loadingStations) {
        for (let tk = 0; tk < s.tracks; tk++) {
          const space = cap(s.code, tk) - countOn(s.code, tk);
          if (space > 0) open.push({ s, tk, space });
        }
      }
      if (!open.length) return;
      const { s, tk, space } = open[ri(0, open.length - 1)];
      const k = Math.min(space, ri(3, 6));
      spawnTrain(
        s.code,
        tk,
        Array.from({ length: k }, () => ({ cargo: pick([["empty", 8], ["foreign", 1]]) }))
      );
      pushEvent("arrive", { stationCode: s.code, count: k }, "info", "locomotive");
    } else if (action === "load") {
      const empties = w.filter((x) => x.cargo === "empty" && x.phase === "parked");
      if (!empties.length) return;
      const pickW = empties.slice(0, ri(2, 5));
      pickW.forEach((x) => {
        const netto = +rnd(58, 71).toFixed(1);
        x.cargo = "coal";
        x.weight_netto = netto;
        x.weight_brutto = +(netto + 23).toFixed(1);
        Object.assign(x, synthesizeBogies(x.weight_brutto));
        x.destination = DEST_CODES[ri(0, DEST_CODES.length - 1)];
        x.dwell_since = nowS();
        x.updated_by = "scale";
        x.status = "loaded";
      });
      const st = STATIONS.find((s) => s.code === pickW[0].st);
      pushEvent("load", { stationCode: st.code, count: pickW.length }, "success", "scale");
      bump();
    } else if (action === "toSort") {
      // pick a loading track whose whole chain is parked and carries coal, send it to the sorting yard
      const candidates = loadingStations.flatMap((s) =>
        Array.from({ length: s.tracks }, (_, tk) => ({ code: s.code, tk }))
      );
      const found = candidates.find(({ code, tk }) => {
        const list = w.filter((x) => x.st === code && x.tk === tk && x.phase !== "departing");
        return (
          list.length >= 2 &&
          list.every((x) => x.phase === "parked") &&
          list.filter((x) => x.cargo !== "empty").length >= 2
        );
      });
      if (!found) return;
      const gone = departTrack(found.code, found.tk);
      if (!gone.length) return;
      const chainData = gone.map((x) => ({
        number: x.number,
        cargo: x.cargo,
        weight_brutto: x.weight_brutto,
        weight_netto: x.weight_netto,
        bogie1_t: x.bogie1_t,
        bogie2_t: x.bogie2_t,
        destination: x.destination,
        owner: x.owner,
      }));
      let dtk = 0, best = -1;
      for (let tk = 0; tk < sorting.tracks; tk++) {
        const free = cap(sorting.code, tk) - countOn(sorting.code, tk);
        if (free > best) { best = free; dtk = tk; }
      }
      schedule(() => spawnTrain(sorting.code, dtk, chainData), 2400);
      pushEvent(
        "transfer",
        { fromStationCode: found.code, toStationCode: sorting.code, count: chainData.length },
        "info",
        "locomotive"
      );
    } else if (action === "departOut") {
      const list = Array.from({ length: sorting.tracks }, (_, tk) => tk).find((tk) => {
        const all = w.filter((x) => x.st === sorting.code && x.tk === tk && x.phase !== "departing");
        return all.length >= 3 && all.every((x) => x.phase === "parked") && all.filter((x) => x.cargo === "coal").length >= 3;
      });
      if (list === undefined) return;
      const gone = departTrack(sorting.code, list);
      if (!gone.length) return;
      const dest = gone[0]?.destination || DEST_CODES[0];
      pushEvent("depart", { destination: dest, count: gone.length }, "success", "locomotive");
    } else if (action === "shunt") {
      // only the tail wagon of a chain can be pulled out — no passing through coupled wagons
      const movable = w.filter(
        (x) => x.st === sorting.code && x.phase === "parked" && x.slot === tailSlot(x.st, x.tk)
      );
      if (!movable.length) return;
      const x = movable[ri(0, movable.length - 1)];
      const g = geos[sorting.code];
      let dtk = ri(0, sorting.tracks - 1);
      if (dtk === x.tk) dtk = (dtk + 1) % sorting.tracks;
      if (countOn(sorting.code, dtk) >= cap(sorting.code, dtk)) return;
      const dslot = countOn(sorting.code, dtk);
      issueRoute(x, g.shuntRoute(x.tk, x.slot, dtk, dslot), "shunting");
      x.tk = dtk;
      x.slot = dslot;
      x.updated_by = "shunt";
      pushEvent("move", { wagonNumber: x.number, trackIdx: dtk }, "info", "shunt");
      bump();
    } else if (action === "ocr") {
      if (ocrRef.current.length < 6) {
        const queued = new Set(ocrRef.current.map((o) => o.wagon_id));
        const candidates = w.filter((x) => x.phase === "parked" && !queued.has(x.id));
        if (!candidates.length) return;
        const target = candidates[ri(0, candidates.length - 1)];
        const confidence = +rnd(0.4, 0.68).toFixed(2);
        target.ocr_confidence = confidence;
        let proposed = target.number;
        if (Math.random() < 0.45) {
          const chars = proposed.split("");
          chars[ri(1, chars.length - 1)] = String(ri(0, 9));
          proposed = chars.join("");
        }
        ocrRef.current = [
          {
            id: uid(),
            wagon_id: target.id,
            ts: new Date().toISOString(),
            proposed_number: proposed,
            confidence,
            photo_url: PHOTO,
            station_code: target.st,
          },
          ...ocrRef.current,
        ];
        pushEvent("ocr", { variant: "lowConfidence" }, "warning", "camera");
        bump();
      }
    }
  }, [geos, cap, countOn, tailSlot, pushEvent, spawnTrain, departTrack, bump]);

  // init + loops
  useEffect(() => {
    seed();
    pushEvent("system", { variant: "started" }, "success");
    bump();
    const tickI = setInterval(tick, 2600);
    const dwellI = setInterval(bump, 5000);
    return () => {
      clearInterval(tickI);
      clearInterval(dwellI);
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- derived yard -----
  const yard = useMemo(() => {
    const t = nowS();
    const stations = STATIONS.map((s, order) => {
      const tracks = [];
      for (let tk = 0; tk < s.tracks; tk++) {
        const ws = wagonsRef.current
          .filter((w) => w.st === s.code && w.tk === tk)
          .sort((a, b) => a.slot - b.slot)
          .map((w) => ({ ...w, dwell_seconds: simDwell(w.dwell_since, t) }));
        const parked = ws.filter((w) => w.phase !== "departing").length;
        const c = cap(s.code, tk);
        tracks.push({ id: `${s.code}-${tk}`, idx: tk, capacity: c, count: parked, overflow: false, wagons: ws });
      }
      return { id: s.code, code: s.code, type: s.type, order, tracks, wagon_count: tracks.reduce((a, b) => a + b.count, 0), alert: false };
    });
    const all = wagonsRef.current;
    const totals = {
      wagons: all.filter((w) => w.phase !== "departing").length,
      coal: all.filter((w) => w.cargo === "coal").length,
      empty: all.filter((w) => w.cargo === "empty").length,
      foreign: all.filter((w) => w.cargo === "foreign").length,
      alerts: all.filter((w) => w.phase !== "departing" && hasBogieImbalance(w)).length,
    };
    return { stations, totals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cap, version]);

  // ----- public actions -----
  const updateWagon = useCallback(
    (id, fields, actorKey = "dispatcher") => {
      const w = wagonsRef.current.find((x) => x.id === id);
      if (!w) return;
      Object.assign(w, fields, { updated_by: actorKey, updated_at: new Date().toISOString() });
      pushEvent("edit", { wagonNumber: w.number, actorKey }, "info", actorKey);
      bump();
    },
    [pushEvent, bump]
  );

  const moveWagon = useCallback(
    (id, tkIdx) => {
      const w = wagonsRef.current.find((x) => x.id === id);
      if (!w) return { ok: false, reason: "notfound" };
      if (tkIdx === w.tk) return { ok: false, reason: "same" };
      if (w.phase !== "parked") return { ok: false, reason: "moving" };
      if (w.slot !== tailSlot(w.st, w.tk)) return { ok: false, reason: "coupled" };
      if (countOn(w.st, tkIdx) >= cap(w.st, tkIdx)) return { ok: false, reason: "full" };
      const g = geos[w.st];
      const dslot = countOn(w.st, tkIdx);
      issueRoute(w, g.shuntRoute(w.tk, w.slot, tkIdx, dslot), "shunting");
      w.tk = tkIdx;
      w.slot = dslot;
      w.updated_by = "dispatcher";
      pushEvent(
        "move",
        { wagonNumber: w.number, trackIdx: tkIdx, actorKey: "dispatcher" },
        "info",
        "dispatcher"
      );
      bump();
      return { ok: true };
    },
    [geos, countOn, cap, tailSlot, pushEvent, bump]
  );

  // worker intake: accept a new train onto a chosen station/track
  const acceptTrain = useCallback(
    (code, tk, trainNo, rows, actorKey = "dispatcher") => {
      const free = cap(code, tk) - countOn(code, tk);
      if (free <= 0) return { ok: false, accepted: 0, free: 0 };
      const take = rows.slice(0, free);
      const data = take.map((r) => {
        const netto = r.cargo === "empty" ? 0 : +r.netto || 0;
        const brutto = r.brutto != null && r.brutto !== ""
          ? +r.brutto
          : +(netto + 23).toFixed(1);
        const bogies =
          r.bogie1_t != null && r.bogie2_t != null
            ? { bogie1_t: +r.bogie1_t, bogie2_t: +r.bogie2_t }
            : synthesizeBogies(brutto, { empty: r.cargo === "empty" });
        return {
          number: r.number,
          cargo: r.cargo,
          weight_netto: netto,
          weight_brutto: +Number(brutto).toFixed(1),
          bogie1_t: bogies.bogie1_t,
          bogie2_t: bogies.bogie2_t,
          destination: r.cargo === "empty" ? EMPTY_DEST : r.destination || EMPTY_DEST,
          owner: r.cargo === "foreign" ? "foreign" : "own",
          ocr_confidence: 1,
          consistTrainNo: trainNo || "",
          note: "",
          updated_by: actorKey,
        };
      });
      spawnTrain(code, tk, data);
      pushEvent(
        "intake",
        { trainNo, stationCode: code, trackIdx: tk, count: take.length, actorKey },
        "success",
        actorKey
      );
      bump();
      return { ok: true, accepted: take.length, free };
    },
    [cap, countOn, spawnTrain, pushEvent, bump]
  );

  const resolveOcr = useCallback(
    (id, number, action, actorKey = "dispatcher") => {
      const item = ocrRef.current.find((x) => x.id === id);
      ocrRef.current = ocrRef.current.filter((x) => x.id !== id);
      if (item) {
        wagonsRef.current = applyOcrResolution(
          wagonsRef.current,
          item,
          number,
          action,
          actorKey
        );
      }
      pushEvent(
        "ocr",
        { variant: "resolved", number, action, actorKey },
        "success",
        actorKey
      );
      bump();
    },
    [pushEvent, bump]
  );

  const reset = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    eventsRef.current = [];
    ocrRef.current = [];
    seed();
    pushEvent("system", { variant: "reset" }, "success");
    bump();
  }, [seed, pushEvent, bump]);

  const exportCSV = useCallback((code) => {
    const s = STATIONS.find((x) => x.code === code) || STATIONS[0];
    const csv = buildExportCSV(wagonsRef.current, code, lang);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `train_${s.code}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [lang]);

  return {
    yard,
    geos,
    events: eventsRef.current,
    ocr: ocrRef.current,
    onDone,
    updateWagon,
    moveWagon,
    acceptTrain,
    resolveOcr,
    reset,
    exportCSV,
  };
}

// Aim for ~65% of the yard's own capacity. Seed sits around half-full (~84 of
// ~172); 65% keeps the demo visibly busy while a typical loading track (cap 10)
// still has room for a 3–6 wagon arrival. Below that, arrivals outweigh
// departOut (weight 6); at capacity they stop so a track cannot overflow.
export function arrivalWeight(occupied, capacity) {
  if (!(capacity > 0) || occupied >= capacity) return 0;
  const fill = occupied / capacity;
  const TARGET = 0.65;
  const HEAVY = 12;
  if (fill < TARGET) return HEAVY;
  return (HEAVY * (1 - fill)) / (1 - TARGET);
}

// tiny helper utilities
function pick(weighted) {
  const total = weighted.reduce((a, [, w]) => a + w, 0);
  let r = Math.random() * total;
  for (const [k, w] of weighted) {
    if ((r -= w) <= 0) return k;
  }
  return weighted[0][0];
}
